// B8 (BETA): offline queue for daily-report saves. Ops are kept in order; a network failure stops the flush (retry
// when back online); a 409 version_conflict parks the op as a conflict with the server copy (never overwritten
// silently); any other server refusal parks it as "failed" with the message. Storage is pluggable: IndexedDB in the
// browser, an in-memory store in unit tests. Limits: one tab at a time; photos are not queued (they need a connection).

const DB_NAME = "erf-field-queue";
const STORE = "ops";

export function memoryStore() {
  const m = new Map();
  return {
    getAll: async () => [...m.values()].sort((a, b) => a.seq - b.seq),
    put: async (op) => { m.set(op.id, op); },
    delete: async (id) => { m.delete(id); },
  };
}

function req(r) {
  return new Promise((res, rej) => { r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error); });
}

export function idbStore() {
  if (typeof indexedDB === "undefined") return null;
  const open = () => {
    const r = indexedDB.open(DB_NAME, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE, { keyPath: "id" });
    return req(r);
  };
  const tx = async (mode, fn) => { const db = await open(); const out = await req(fn(db.transaction(STORE, mode).objectStore(STORE))); db.close(); return out; };
  return {
    getAll: async () => (await tx("readonly", (s) => s.getAll())).sort((a, b) => a.seq - b.seq),
    put: (op) => tx("readwrite", (s) => s.put(op)),
    delete: (id) => tx("readwrite", (s) => s.delete(id)),
  };
}

const isNetworkError = (e) => !e?.response;

export function createQueue(store) {
  let seq = Date.now();
  return {
    // op: { kind: "create" | "patch" | "submit", pid?, reportId?, version?, body, clientDraftId }
    enqueue: async (op) => {
      const row = { ...op, id: op.id || `op-${(seq += 1)}`, seq: (seq += 1), status: "pending", queued_at: new Date().toISOString() };
      await store.put(row);
      return row;
    },
    list: () => store.getAll(),
    remove: (id) => store.delete(id),
    // send(op) -> server response data. Returns { synced, conflicts, failed, stopped }.
    flush: async (send) => {
      const out = { synced: [], conflicts: [], failed: [], stopped: false };
      let createdId = {};
      for (const op of await store.getAll()) {
        if (op.status !== "pending") continue;
        const ready = op.reportId || createdId[op.clientDraftId] ? { ...op, reportId: op.reportId || createdId[op.clientDraftId] } : op;
        try {
          const data = await send(ready);
          if (op.kind === "create" && data?.id) createdId[op.clientDraftId] = data.id;
          await store.delete(op.id);
          out.synced.push({ op: ready, data });
        } catch (e) {
          if (isNetworkError(e)) { out.stopped = true; break; }
          const d = e.response.data?.detail || {};
          const parked = { ...ready, status: d.code === "version_conflict" ? "conflict" : "failed", error: d.message || "Refused by the server.", server: d.server || null };
          await store.put(parked);
          (parked.status === "conflict" ? out.conflicts : out.failed).push(parked);
        }
      }
      return out;
    },
  };
}

export function sendOp(api) {
  return async (op) => {
    if (op.kind === "create") return (await api.post(`/field/projects/${op.pid}/reports`, { ...op.body, client_draft_id: op.clientDraftId })).data;
    if (op.kind === "patch") return (await api.patch(`/field/reports/${op.reportId}`, { ...op.body, version: op.version })).data;
    if (op.kind === "submit") return (await api.post(`/field/reports/${op.reportId}/submit`, { version: op.version })).data;
    throw new Error(`unknown op ${op.kind}`);
  };
}

// Local draft autosave (every keystroke) — survives a reload or a dead battery.
export const draftKey = (pid, day) => `erf.field.draft.${pid}.${day}`;
export const saveLocalDraft = (pid, day, v) => { try { localStorage.setItem(draftKey(pid, day), JSON.stringify({ ...v, saved_at: new Date().toISOString() })); } catch (_) { /* storage full */ } };
export const loadLocalDraft = (pid, day) => { try { return JSON.parse(localStorage.getItem(draftKey(pid, day)) || "null"); } catch (_) { return null; } };
export const clearLocalDraft = (pid, day) => { try { localStorage.removeItem(draftKey(pid, day)); } catch (_) { /* ignore */ } };
